import { useRef, useCallback, useEffect, useContext } from 'react';
import ddt from '@wix/da-ddt';
import { useBiLogger } from './useBiLogger';
import { getClickType, MouseButton } from '../helpers';
import { BiContext } from '../BiLogger.context';
import type { BiEvent } from '../events/base';

const logger = ddt.createLogger('bilogger');

/** Returns true if the click event is trusted and used the middle or primary/left button */
export function isLoggableClickEvent(event: MouseEvent) {
  return (
    event.isTrusted &&
    [MouseButton.Main, MouseButton.Auxiliary].includes(getClickType(event))
  );
}

function getTrackedElementAndEvent(trackedElement: HTMLElement | undefined) {
  let eventType = 'mousedown';
  switch (trackedElement?.tagName.toLowerCase()) {
    case 'input':
      eventType = 'click';
      break;
    case 'option':
      eventType = 'change';
      // change events for option are at the select parent
      if (trackedElement.parentElement) {
        trackedElement = trackedElement.parentElement;
      }
      break;
    default:
  }
  return { eventType, trackedElement };
}

export function useBiClickLogger(biData?: BiEvent, stopPropagation?: boolean) {
  const logEvent = useBiLogger();
  const trackingRef = useRef<HTMLElement>(undefined);
  const setTrackingRef = useCallback((node: HTMLElement | null) => {
    trackingRef.current = node || undefined;
  }, []);
  const biXray = false; // useFlag('bi_xray');
  const biContext = useContext(BiContext);

  useEffect(() => {
    if (!biData) {
      return;
    }

    const { eventType, trackedElement } = getTrackedElementAndEvent(
      trackingRef.current
    );

    if (!trackedElement) {
      if (process.env.NODE_ENV !== 'production') {
        logger.error('BiLogger childNode ref has not been set up');
      }
      return;
    }

    const eventHandler = (e: MouseEvent) => {
      switch (eventType) {
        case 'mousedown':
          // only allow left/mid clicks logging
          if (!isLoggableClickEvent(e)) {
            return true;
          }
          break;
        case 'change':
          if (
            eventType === 'change' &&
            biData._value !== (trackedElement as HTMLSelectElement).value
          ) {
            return true;
          }
          break;
        default:
          break;
      }

      logEvent(biData);

      if (stopPropagation) {
        e.stopPropagation();
      }
    };

    // leave capture mode off, so that descendant elements can stop propagation
    // and fire their own bi events if needed.
    trackedElement.addEventListener(eventType, eventHandler, false);
    return () =>
      trackedElement &&
      trackedElement.removeEventListener(eventType, eventHandler, false);
  }, [biData, logEvent, trackingRef, stopPropagation]);

  useEffect(() => {
    if (!biXray) {
      return;
    }

    const { trackedElement } = getTrackedElementAndEvent(trackingRef.current);
    if (!trackedElement) {
      return;
    }

    trackedElement.setAttribute(
      'data-bidata',
      JSON.stringify({ ...biContext, ...biData })
    );

    return () => trackedElement.removeAttribute('data-bidata');
  }, [biXray, biContext, biData]);

  return {
    setTrackingRef,
  };
}
